<template>
  <v-dialog v-model="showDialog" max-width="400" persistent>
    <v-card class="customBorderRadius">
      <v-card-title class="primarygrad" style="border-radius: 12px 12px 0 0;">
        <v-icon left color="white">mdi-account-plus</v-icon>
        <span class="white--text">Save New Contact</span>
      </v-card-title>
      <v-card-text class="pt-6">
        <p class="mb-4 grey--text text--darken-1">
          This number is not saved. Would you like to save it as a contact?
        </p>
        <v-text-field
          v-model="phoneNumber"
          label="Phone Number"
          prepend-inner-icon="mdi-phone"
          outlined
          dense
          readonly
          class="customBorderRadius mb-2"
        />
        <v-text-field
          v-model="contactName"
          label="Contact Name"
          prepend-inner-icon="mdi-account"
          outlined
          dense
          autofocus
          class="customBorderRadius"
          placeholder="Enter contact name"
          @keyup.enter="saveContact"
        />
        <v-select
          v-model="selectedListId"
          :items="availableLists"
          item-text="name"
          item-value="id"
          label="Select List"
          prepend-inner-icon="mdi-format-list-bulleted"
          outlined
          dense
          class="customBorderRadius"
          :loading="loadingLists"
        />
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-btn
          text
          color="grey"
          class="customBorderRadius"
          @click="skipSave"
        >
          Skip
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          text
          color="red"
          class="customBorderRadius"
          @click="cancelSave"
        >
          Cancel
        </v-btn>
        <v-btn
          depressed
          color="primarygrad"
          class="customBorderRadius"
          :loading="isSaving"
          :disabled="!contactName.trim() || !selectedListId"
          @click="saveContact"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "SaveContactDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    phone: {
      type: String,
      default: "",
    },
    // Whether to show disposition dialog after save/skip
    showDispositionAfter: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      contactName: "",
      selectedListId: null,
      availableLists: [],
      loadingLists: false,
      isSaving: false,
    };
  },

  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    phoneNumber() {
      return this.phone || this.$store.state.dialer.pendingContactPhone || "";
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        // Fetch lists when dialog opens
        this.fetchLists();
        this.resetForm();
      }
    },
  },

  methods: {
    async fetchLists() {
      this.loadingLists = true;
      try {
        // Get teams from store
        const teams = this.$store.state.teams || [];
        
        if (!teams || teams.length === 0) {
          console.warn("No teams available for fetching lists");
          this.availableLists = [];
          return;
        }

        // Get team IDs (exclude 'allTeams')
        const teamIds = teams
          .filter((t) => t && t.id !== "allTeams")
          .map((t) => t.id);

        if (teamIds.length === 0) {
          console.warn("No valid team IDs for fetching lists");
          this.availableLists = [];
          return;
        }

        // Use the lists/fetch API which works with teams
        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/lists/fetch`,
          { teams: teamIds }
        );

        this.availableLists = response.body || response.data || [];
        console.log("📋 Loaded lists for save contact:", this.availableLists.length);

        // Auto-select first list if only one available
        if (this.availableLists.length === 1) {
          this.selectedListId = this.availableLists[0].id;
        }
      } catch (err) {
        console.error("Error fetching lists:", err);
        this.availableLists = [];
      } finally {
        this.loadingLists = false;
      }
    },

    async saveContact() {
      if (!this.contactName.trim() || !this.selectedListId) return;

      this.isSaving = true;
      try {
        const companyDetails = this.$store.getters.companyDetails;
        const companyId = companyDetails && companyDetails.id;
        const user = this.$store.state.user;
        const userId = user && user.id;

        const newContact = {
          phone: this.phoneNumber,
          name: this.contactName.trim(),
          companyId: companyId,
          accountsId: userId,
          listId: this.selectedListId,
          dispo: "NEW",
          called: false,
          customFields: {},
        };

        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/contacts`,
          newContact
        );

        console.log("✅ Contact saved:", response.body || response.data);

        // Update the last call contact name in store
        this.$store.commit("dialer/SET_LAST_CALL_DATA", {
          duration: this.$store.state.dialer.lastCallDuration,
          number: this.$store.state.dialer.lastCallNumber,
          contactName: this.contactName.trim(),
          direction: this.$store.state.dialer.lastCallDirection,
        });

        this.$swal({
          icon: "success",
          title: "Contact saved successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });

        this.closeAndProceed();
      } catch (err) {
        console.error("Error saving contact:", err);
        this.$swal({
          icon: "error",
          title: "Failed to save contact",
          text: err.message || "Unknown error",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } finally {
        this.isSaving = false;
      }
    },

    skipSave() {
      console.log("⏭️ Skipping save contact");
      this.closeAndProceed();
    },

    cancelSave() {
      console.log("❌ Cancelled save contact");
      this.showDialog = false;
      this.resetForm();
      this.$store.commit("dialer/CLEAR_PENDING_CONTACT_DATA");
      this.$emit("cancelled");
    },

    closeAndProceed() {
      this.showDialog = false;
      this.resetForm();

      // Clear pending contact data
      this.$store.commit("dialer/CLEAR_PENDING_CONTACT_DATA");

      // Show disposition dialog if needed
      if (this.showDispositionAfter) {
        console.log("📋 Now showing disposition dialog");
        this.$store.commit("dialer/SET_SHOW_DISPOSITION_DIALOG", true);
      }

      this.$emit("completed");
    },

    resetForm() {
      this.contactName = "";
      this.selectedListId = null;
    },
  },
};
</script>

<style scoped>
.customBorderRadius {
  border-radius: 12px !important;
}
</style>
